import { useEffect, useState } from "react";

import MedicineCatalogService from "../medicines/MedicineCatalogService";
import {
  extractSaltNamesFromCatalogRow,
  fetchSaltAllergies,
  findSaltAllergyMatch,
} from "../medicines/saltAllergyCache";

/**
 * Reviewer-side salt-allergy guard for the transcript flow. Mirrors
 * MedicineForm's Add-time check (see saltAllergyCache.findSaltAllergyMatch)
 * so a Med row captured by the LLM surfaces the SAME warning a
 * clinician would see when picking it from the standalone catalog.
 *
 * Standalone blocks Save via a modal (AllergyGuardDialog); ReviewSave
 * renders as a per-row warning strip instead — the reviewer sees N
 * staged rows at once and modally interrupting each match would be
 * unusable. The reviewer still sees the row before Save & next.
 *
 * Design notes:
 *   • Salt allergies are fetched once per patient via the shared cache.
 *   • For each captured row, the medicine catalog is searched by the
 *     captured `medicine` name; the TOP result is used to source
 *     `saltNames`. This mirrors the autocomplete's own top-of-list
 *     rendering — the reviewer would have picked that row too. When
 *     no result comes back the row simply doesn't warn.
 *   • Per-name lookups are cached in a module-level Map so re-renders
 *     don't re-fetch. Cache key is the lower-cased trimmed name.
 *   • Empty patientId / empty items short-circuits to no work.
 */

/** name(lower) -> string[] of lowercased salt names. Module-level so a
 *  second render of the same review session doesn't repeat the search. */
const saltNameCache = new Map();

const norm = (s) => String(s || "").trim().toLowerCase();

const fetchSaltNamesForName = (name) => {
  const key = norm(name);
  if (!key) return Promise.resolve([]);
  if (saltNameCache.has(key)) return Promise.resolve(saltNameCache.get(key));
  return new Promise((resolve) => {
    new MedicineCatalogService().search(
      key,
      (rows) => {
        const top = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
        const salts = top ? extractSaltNamesFromCatalogRow(top) : [];
        saltNameCache.set(key, salts);
        resolve(salts);
      },
      () => {
        // Non-fatal — treat as no salts. Empty result won't fire a warning;
        // the reviewer can still edit + re-pick from the standalone catalog
        // via click-to-edit, which runs the real Add-time guard.
        saltNameCache.set(key, []);
        resolve([]);
      }
    );
  });
};

/**
 * useSaltAllergyCheck — returns `checks`, an object keyed by captured
 * item id, each value the shape saltAllergyCache.findSaltAllergyMatch
 * returns (or null). Renders no fallback UI; leave the row unbadged
 * until the promise resolves.
 */
export const useSaltAllergyCheck = (patientId, items) => {
  const [checks, setChecks] = useState({});

  // Rerun when patient changes OR the set of distinct medicine names on
  // screen changes (not on every render). Reads the captured payload's
  // `medicine` / `name` / `value` in that order to mirror
  // cellsForMedicine's lookup.
  const namesKey = (items || [])
    .map((it) => {
      const p = (it && it.current) || {};
      return norm(p.medicine || p.name || p.value || "");
    })
    .filter(Boolean)
    .sort()
    .join("|");

  useEffect(() => {
    let cancelled = false;
    if (!patientId || !items || items.length === 0) {
      setChecks({});
      return () => {};
    }

    fetchSaltAllergies(patientId).then((saltAllergies) => {
      if (cancelled) return;
      if (!saltAllergies || saltAllergies.length === 0) {
        setChecks({});
        return;
      }

      // Fan out per-row salt-name lookup. Concurrent duplicates dedupe
      // through saltNameCache automatically.
      Promise.all(
        (items || []).map((it) => {
          const p = (it && it.current) || {};
          const name = p.medicine || p.name || p.value || "";
          return fetchSaltNamesForName(name).then((salts) => ({
            id: it.id,
            match: findSaltAllergyMatch(salts, saltAllergies),
          }));
        })
      ).then((results) => {
        if (cancelled) return;
        const next = {};
        for (const { id, match } of results) {
          if (match) next[id] = match;
        }
        setChecks(next);
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, namesKey]);

  return checks;
};
